import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Kanban,
  FileText,
  Users,
  Target,
  Truck,
  Wrench,
  DollarSign,
  Brain,
  LogOut,
  TicketCheck,
  UserCog,
  Mail,
  MessageCircle,
  Clock,
  Building2,
  AlertTriangle,
  Boxes,
  ShieldCheck,
  Wallet,
  Search,
  Briefcase,
  Database,
  Settings,
  BarChart3,
  ChevronRight,
  PieChart,
  ClipboardList,
  Package,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./Logo";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { FilialSelector } from "./FilialSelector";
import { GlobalSearch } from "./GlobalSearch";
import { NotificationBell } from "./NotificationBell";
import { SlaAlertBell } from "./SlaAlertBell";

const ROLE_LABEL = {
  diretor: "Diretor",
  financeiro: "Financeiro",
  vendedor: "Vendedor",
  operador: "Operador",
  oficina: "Oficina",
  supervisao: "Supervisão",
  gerente: "Gerente",
  cliente: "Cliente",
};

// ============================================================
// MENU ESTRUTURADO POR DEPARTAMENTO (6 áreas)
// Apenas reorganização — NÃO altera rotas existentes
// ============================================================
const TOP_LEVEL = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["diretor","financeiro","operador","oficina","supervisao","gerente"] },
  { to: "/app/dashboard/executivo", label: "Dashboard Executivo", icon: PieChart, roles: ["diretor","gerente","supervisao","financeiro"] },
  { to: "/app/meu-dashboard", label: "Meu Dashboard", icon: LayoutDashboard, roles: ["diretor","vendedor","supervisao","gerente"] },
  { to: "/app/busca", label: "Busca Global", icon: Search, roles: ["diretor","supervisao","gerente","operador","oficina","financeiro","vendedor"] },
];

const GRUPOS = [
  {
    key: "comercial",
    label: "Comercial",
    icon: Briefcase,
    roles: ["diretor","vendedor","financeiro","supervisao","gerente"],
    items: [
      { to: "/app/funil", label: "Funil de Vendas", icon: Kanban, roles: ["diretor","vendedor","financeiro","supervisao","gerente"] },
      { to: "/app/leads", label: "Leads", icon: UserCog, roles: ["diretor","vendedor","financeiro","supervisao","gerente"] },
      { to: "/app/propostas", label: "Propostas", icon: FileText, roles: ["diretor","vendedor","financeiro","supervisao","gerente","operador"] },
      { to: "/app/clientes", label: "Clientes", icon: Users, roles: ["diretor","vendedor","financeiro","supervisao","gerente"] },
      { to: "/app/metas", label: "Metas", icon: Target, roles: ["diretor","vendedor","financeiro","supervisao","gerente"] },
      { to: "/app/metas/cadastro", label: "Cadastrar Metas", icon: Target, roles: ["diretor","supervisao"] },
      { to: "/app/dashboard-equipe", label: "Painel da Equipe", icon: Users, roles: ["diretor","supervisao"] },
      { to: "/app/minhas-comissoes", label: "Minhas Comissões", icon: DollarSign, roles: ["diretor","vendedor","supervisao","gerente","financeiro","operador"] },
    ],
  },
  {
    key: "logistica",
    label: "Logística",
    icon: Truck,
    roles: ["diretor","supervisao","operador","oficina","gerente","financeiro","vendedor"],
    items: [
      { to: "/app/roteirizacao", label: "Operações & Roteirização", icon: Truck, roles: ["diretor","supervisao","gerente","operador","oficina"] },
      { to: "/app/logistica/equipamentos", label: "Logística / Oficina", icon: Wrench, roles: ["diretor","supervisao","oficina","operador"] },
      { to: "/app/oficina", label: "Oficina", icon: Wrench, roles: ["diretor","operador","oficina","supervisao"] },
      { to: "/app/oficina/estoque", label: "Estoque da Oficina", icon: Wrench, roles: ["diretor","gerente","supervisao","operador","oficina","financeiro"] },
      { to: "/app/oficina/associacao", label: "Associar Equipamentos", icon: Wrench, roles: ["diretor","supervisao","oficina","operador","gerente"] },
      { to: "/app/frota", label: "Frota", icon: Truck, roles: ["diretor","operador","financeiro","oficina","supervisao"] },
      { to: "/app/inventario", label: "Inventário", icon: Boxes, roles: ["diretor","operador","financeiro","vendedor","oficina","supervisao"] },
      { to: "/app/tickets", label: "Tickets", icon: TicketCheck, roles: ["diretor","operador","financeiro","oficina","supervisao"] },
      { to: "/app/tickets-ops", label: "Tickets / Operações", icon: TicketCheck, roles: ["diretor","gerente","supervisao","operador","oficina","vendedor","financeiro"] },
      { to: "/app/posvenda", label: "Pós-venda LAB", icon: Wrench, roles: ["diretor","gerente","supervisao","operador","oficina","vendedor","financeiro"] },
      { to: "/app/posvenda/propostas", label: "Propostas Pós-venda", icon: ClipboardList, roles: ["diretor","gerente","supervisao","operador","oficina","vendedor","financeiro"] },
      { to: "/app/posvenda/ordens-servico", label: "Ordens de Serviço (OS)", icon: FileText, roles: ["diretor","gerente","supervisao","operador","oficina","vendedor","financeiro"] },
      { to: "/app/posvenda/separacoes", label: "Separações de Peças", icon: Package, roles: ["diretor","gerente","supervisao","operador","oficina"] },
      // Comissões Pós-venda removidas do sidebar em iter56 — agora estão em "Minhas Comissões" (aba Pós-venda 3%)
      // Extraviados foi movido para sub-aba dentro de /app/tickets-ops (Fase A v2)
    ],
  },
  {
    key: "cadastros",
    label: "Cadastros",
    icon: Database,
    roles: ["diretor","supervisao","gerente"],
    items: [
      { to: "/app/admin/clientes", label: "Clientes (Admin)", icon: Users, roles: ["diretor","supervisao"] },
      { to: "/app/admin/usuarios", label: "Usuários", icon: ShieldCheck, roles: ["diretor","supervisao"] },
      { to: "/app/admin/unidades-usuarios", label: "Unidades × Usuários", icon: Building2, roles: ["diretor","supervisao"] },
      { to: "/app/configuracoes/unidades", label: "Unidades", icon: Building2, roles: ["diretor","supervisao"] },
      { to: "/app/configuracoes/filiais", label: "Filiais", icon: Building2, roles: ["diretor"] },
      { to: "/app/proposta/catalogo", label: "Catálogo de Equipamentos", icon: Boxes, roles: ["diretor","supervisao","gerente"] },
      { to: "/app/cadastro/pecas-servicos", label: "Peças e Serviços (Pós-venda)", icon: Wrench, roles: ["diretor","gerente"] },
      { to: "/app/proposta/templates", label: "Templates de Proposta", icon: FileText, roles: ["diretor","supervisao","gerente"] },
      { to: "/app/configuracoes/modelos", label: "Modelos de Contrato", icon: FileText, roles: ["diretor","supervisao","gerente"] },
    ],
  },
  {
    key: "financeiro",
    label: "Financeiro",
    icon: Wallet,
    roles: ["diretor","financeiro","supervisao","gerente","vendedor"],
    items: [
      { to: "/app/financeiro", label: "Financeiro", icon: DollarSign, roles: ["diretor","financeiro","supervisao"] },
      { to: "/app/financeiro-comercial", label: "Financeiro Comercial", icon: DollarSign, roles: ["diretor","supervisao","financeiro"] },
      { to: "/app/financeiro/condicoes", label: "Condições de Pagamento", icon: Wallet, roles: ["diretor","financeiro","supervisao","gerente","vendedor"] },
    ],
  },
  {
    key: "sistema",
    label: "Sistema",
    icon: Settings,
    roles: ["diretor","supervisao","financeiro"],
    items: [
      { to: "/app/email-log", label: "Log de e-mails", icon: Mail, roles: ["diretor","financeiro"] },
      { to: "/app/whatsapp-log", label: "Log WhatsApp", icon: MessageCircle, roles: ["diretor","financeiro"] },
      { to: "/app/jobs", label: "Cron Jobs", icon: Clock, roles: ["diretor","financeiro"] },
    ],
  },
  {
    key: "diretoria",
    label: "Diretoria",
    icon: BarChart3,
    roles: ["diretor","supervisao","financeiro","vendedor"],
    items: [
      { to: "/app/ia", label: "Insights IA", icon: Brain, roles: ["diretor","financeiro","vendedor","supervisao"] },
    ],
  },
];

const PORTAL_NAV = [
  { to: "/portal/dashboard", label: "Meu painel", icon: LayoutDashboard },
  { to: "/portal/tickets", label: "Meus chamados", icon: TicketCheck },
  { to: "/portal/financeiro", label: "Financeiro", icon: DollarSign },
  { to: "/portal/contratos", label: "Contratos", icon: FileText },
  { to: "/portal/equipamentos", label: "Equipamentos", icon: Truck },
];

// ============================================================
// Componentes auxiliares
// ============================================================
const NavItem = ({ to, label, icon: Icon }) => (
  <NavLink
    to={to}
    data-testid={`nav-${label
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")}`}
    className={({ isActive }) =>
      `sidebar-link flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium text-white/75 ${isActive ? "active" : ""}`
    }
  >
    <Icon size={15} className="shrink-0" />
    <span className="truncate">{label}</span>
  </NavLink>
);

const GroupSection = ({ group, userRole, openMap, toggle }) => {
  const visibleItems = group.items.filter((it) => !it.roles || it.roles.includes(userRole));
  if (visibleItems.length === 0) return null;
  const open = openMap[group.key];
  const Icon = group.icon;
  return (
    <div data-testid={`sidebar-group-${group.key}`} className="space-y-0.5">
      <button
        type="button"
        onClick={() => toggle(group.key)}
        className="sidebar-group-header w-full flex items-center justify-between px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-white/45 hover:text-white/80"
        data-testid={`sidebar-group-toggle-${group.key}`}
      >
        <span className="flex items-center gap-2">
          <Icon size={11} />
          {group.label}
          <span className="text-white/30 font-normal normal-case tracking-normal text-[10px]">
            ({visibleItems.length})
          </span>
        </span>
        <ChevronRight size={12} className={`sidebar-chevron ${open ? "open" : ""}`} />
      </button>
      {open && (
        <div className="space-y-0.5 pb-1">
          {visibleItems.map((it) => <NavItem key={it.to} {...it} />)}
        </div>
      )}
    </div>
  );
};

export const AppShell = ({ children, portal = false }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  // Open/close por grupo — persistir no localStorage
  const [openMap, setOpenMap] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("lab_sidebar_groups") || "{}");
      return {
        comercial: saved.comercial ?? true,
        logistica: saved.logistica ?? true,
        cadastros: saved.cadastros ?? false,
        financeiro: saved.financeiro ?? true,
        sistema: saved.sistema ?? false,
        diretoria: saved.diretoria ?? false,
      };
    } catch {
      return { comercial: true, logistica: true, cadastros: false, financeiro: true, sistema: false, diretoria: false };
    }
  });
  const toggle = (key) => setOpenMap((prev) => {
    const next = { ...prev, [key]: !prev[key] };
    try { localStorage.setItem("lab_sidebar_groups", JSON.stringify(next)); } catch {}
    return next;
  });

  if (!user) return null;

  const initials = (user.name || "?")
    .split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

  const topItems = TOP_LEVEL.filter((n) => !n.roles || n.roles.includes(user.role));
  const visibleGroups = GRUPOS.filter((g) => !g.roles || g.roles.includes(user.role));

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* === Sidebar premium === */}
      <aside
        className="w-64 shrink-0 text-white flex flex-col relative"
        style={{
          background:
            "linear-gradient(180deg, #1B3A6B 0%, #16315A 100%)",
        }}
        data-testid="app-sidebar"
      >
        {/* Linha laranja sutil no topo */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-400/60 to-transparent" />

        <div className="px-5 py-5 border-b border-white/8">
          <Logo size="sm" variant="dark" />
          <div className="mt-1.5 flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-orange-400 animate-pulse" />
            <span className="text-[9px] uppercase tracking-[0.25em] text-white/50 font-semibold">
              {portal ? "Portal Cliente" : "Plataforma LAB"}
            </span>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2.5 space-y-1">
          {portal ? (
            PORTAL_NAV.map((n) => <NavItem key={n.to} {...n} />)
          ) : (
            <>
              {/* Top-level (sem grupo) */}
              {topItems.length > 0 && (
                <div className="space-y-0.5 pb-2 mb-1 border-b border-white/8">
                  {topItems.map((it) => <NavItem key={it.to} {...it} />)}
                </div>
              )}

              {/* Grupos departamentais */}
              {visibleGroups.map((g) => (
                <GroupSection key={g.key} group={g} userRole={user.role} openMap={openMap} toggle={toggle} />
              ))}
            </>
          )}
        </nav>

        {/* Footer com user info */}
        <div className="p-3 border-t border-white/10 bg-black/10">
          <div className="flex items-center gap-3 mb-3 px-1">
            <Avatar className="h-9 w-9 border-2" style={{ borderColor: "#F97316" }}>
              <AvatarFallback className="bg-white/10 text-white text-xs font-semibold">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate text-white" data-testid="user-name">
                {user.name}
              </div>
              <div className="text-[10px] text-white/55 uppercase tracking-wider font-semibold">
                {ROLE_LABEL[user.role] || user.role}
              </div>
            </div>
          </div>
          <Button
            data-testid="logout-button"
            variant="outline"
            size="sm"
            className="w-full border-white/15 bg-white/5 text-white hover:bg-white/10 hover:text-white hover:border-white/25 transition-all"
            onClick={() => { logout(); nav("/login"); }}
          >
            <LogOut size={13} className="mr-2" />
            Sair
          </Button>
        </div>
      </aside>

      {/* === Main === */}
      <main className="flex-1 min-w-0 overflow-x-hidden bg-slate-50">
        <div
          className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/85 backdrop-blur-md px-6 sm:px-10 py-3 flex items-center justify-between gap-3"
          data-testid="app-topbar"
        >
          <GlobalSearch />
          <div className="flex items-center gap-2">
            <SlaAlertBell />
            <NotificationBell />
            <FilialSelector />
          </div>
        </div>
        <div className="px-6 sm:px-10 py-6 fade-in">{children}</div>
      </main>
    </div>
  );
};
